import * as React from 'react';
import { CalendarBlank, Clock, WarningCircle } from '@phosphor-icons/react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { ja } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { useHolidays } from '@/hooks/useHolidays';

/* ──────────────────────────────────────────────────────────────────
 * <DateField value="YYYY-MM-DD" onChange={(v) => …} min max />
 *
 * Provides BOTH manual typing AND a calendar picker on the same control.
 * – Text input accepts free-typing in YYYY/MM/DD or YYYY-MM-DD (any
 *   non-digit separator works). When the typed value is a complete &
 *   valid date, onChange fires with `YYYY-MM-DD`.
 * – Calendar icon button opens a Radix Popover with shadcn Calendar.
 *   Selecting a date updates the text input and fires onChange.
 * ────────────────────────────────────────────────────────────────── */

const pad = (n) => String(n).padStart(2, '0');
const isoFromDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const dateFromIso = (s) => {
  if (!s) return undefined;
  const [y, m, d] = s.split('-').map((x) => parseInt(x, 10));
  if (!y || !m || !d) return undefined;
  const dt = new Date(y, m - 1, d);
  return isNaN(dt.getTime()) ? undefined : dt;
};

// Format incoming "YYYY-MM-DD" → "YYYY/MM/DD" for the visible text
const isoToDisplay = (iso) => (iso ? iso.replace(/-/g, '/') : '');
// Parse arbitrary user input → "YYYY-MM-DD" (or '' if incomplete/invalid)
const parseToIso = (raw) => {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  if (digits.length !== 8) return ''; // need YYYYMMDD
  const y = digits.slice(0, 4);
  const m = digits.slice(4, 6);
  const d = digits.slice(6, 8);
  const dt = new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
  if (isNaN(dt.getTime())) return '';
  // Round-trip validation (rejects 2026/02/31 etc.)
  if (dt.getFullYear() !== parseInt(y, 10) || dt.getMonth() !== parseInt(m, 10) - 1 || dt.getDate() !== parseInt(d, 10)) return '';
  return `${y}-${m}-${d}`;
};

export const DateField = React.forwardRef(function DateField(
  { value = '', onChange, min, max, disabled, className, inputClassName, placeholder = 'yyyy/mm/dd', testId, title, id, name, required,
    disableNonWorking = false, warnNonWorking = false, snapForward = false },
  ref
) {
  const [text, setText] = React.useState(isoToDisplay(value));
  const [open, setOpen] = React.useState(false);
  // 表示月。開くたびに今の値（無ければ今日）に合わせる。
  const [month, setMonth] = React.useState(() => dateFromIso(value) || new Date());

  // Keep local text in sync when parent value changes externally
  React.useEffect(() => {
    setText(isoToDisplay(value));
  }, [value]);

  React.useEffect(() => {
    if (open) setMonth(dateFromIso(value) || new Date());
  }, [open, value]);

  // 平日・祝日 対応 — passive hint OR hard-disable, controlled by props.
  const yearFromValue = value ? parseInt(String(value).slice(0, 4), 10) : new Date().getFullYear();
  const { isNonWorking, nonWorkingReason, nextWorkingDay } = useHolidays(yearFromValue);
  const nwReason = value ? nonWorkingReason(value) : null;
  const showNonWorking = !!nwReason && (disableNonWorking || warnNonWorking);
  const nonWorkingBlocks = !!nwReason && disableNonWorking;

  const emitChange = (iso) => {
    if (!iso) { onChange?.(iso); return; }
    if (snapForward) {
      const reason = nonWorkingReason(iso);
      if (reason) {
        onChange?.(nextWorkingDay(iso, 'forward'));
        return;
      }
    }
    onChange?.(iso);
  };

  // 打っている途中の数字を確定させると、途中の並びが別の日付として親に入ってしまう。
  // 確定は blur / Enter のときだけ。空にしたら空のまま親へ返す（期限を外せる）。
  const handleTextChange = (e) => setText(e.target.value);

  const commit = () => {
    if (!text.trim()) {
      setText('');
      if (value) onChange?.('');
      return;
    }
    const iso = parseToIso(text);
    if (iso) {
      setText(isoToDisplay(iso));
      if (iso !== value) emitChange(iso);
    } else {
      setText(isoToDisplay(value));   // 打ち間違いは元の値に戻す
    }
  };

  const handleBlur = () => commit();
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); commit(); }
    if (e.key === 'Escape' && open) setOpen(false);
  };

  const handlePick = (d) => {
    if (!d) return;
    const iso = isoFromDate(d);
    setText(isoToDisplay(iso));
    emitChange(iso);
    setOpen(false);
  };

  const selectedDate = dateFromIso(value);
  const minDate = dateFromIso(min);
  const maxDate = dateFromIso(max);
  const hintText = nwReason
    ? `${nwReason.name}（${nwReason.reason === 'weekend' ? '休日' : nwReason.reason === 'holiday' ? '祝日' : '会社休業日'}）`
    : null;

  return (
    <div className={cn('relative inline-flex w-full items-center', className)}>
      <input
        ref={ref}
        type="text"
        inputMode="numeric"
        id={id}
        name={name}
        required={required}
        value={text}
        onChange={handleTextChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        data-testid={testId}
        data-nonworking={nwReason ? nwReason.reason : undefined}
        title={hintText || title}
        aria-invalid={nonWorkingBlocks || undefined}
        className={cn(
          "h-9 w-full rounded-md border border-zinc-300 bg-white px-3 pr-16 text-sm placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50",
          showNonWorking && (
            nonWorkingBlocks
              ? 'border-red-300 ring-1 ring-red-100 bg-red-50/40 text-red-700'
              : 'border-amber-300 ring-1 ring-amber-100 bg-amber-50/40 text-amber-800'
          ),
          inputClassName
        )}
      />
      {showNonWorking && (
        <span
          data-testid={testId ? `${testId}-nonworking` : undefined}
          className="absolute right-9 pointer-events-none text-[10px] text-amber-700 font-semibold flex items-center gap-0.5"
          title={hintText || undefined}
        >
          <WarningCircle size={11} weight="fill" />
        </span>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            aria-label="カレンダーを開く"
            className="absolute right-1 inline-flex h-7 w-7 items-center justify-center rounded text-zinc-500 hover:bg-zinc-100 hover:text-zinc-800 disabled:opacity-50"
          >
            <CalendarBlank size={16} weight="duotone" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          side="bottom"
          sideOffset={4}
          collisionPadding={12}
          avoidCollisions
          className="w-auto p-0"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <Calendar
            mode="single"
            locale={ja}
            selected={selectedDate}
            onSelect={handlePick}
            month={month}
            onMonthChange={setMonth}
            formatters={{
              formatCaption: (d) => `${d.getFullYear()}年${d.getMonth() + 1}月`,
              formatWeekdayName: (d) => ['日', '月', '火', '水', '木', '金', '土'][d.getDay()],
            }}
            disabled={(date) => (minDate && date < minDate) || (maxDate && date > maxDate) || (disableNonWorking && isNonWorking(date))}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
});

/* ──────────────────────────────────────────────────────────────────
 * <TimeField value="HH:MM" onChange={(v) => …} />
 *
 * Free-typing in any HH:MM format. A dropdown picker lists times in
 * 15-minute increments — clicking inserts that time and closes the
 * popover.
 * ────────────────────────────────────────────────────────────────── */

const TIME_SLOTS = (() => {
  const out = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
      out.push(`${pad(h)}:${pad(m)}`);
    }
  }
  return out;
})();

const parseToHHMM = (raw) => {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  if (digits.length < 3) return '';
  const hh = parseInt(digits.slice(0, 2), 10);
  const mm = parseInt(digits.slice(2, 4).padEnd(2, '0'), 10);
  if (isNaN(hh) || isNaN(mm) || hh > 23 || mm > 59) return '';
  return `${pad(hh)}:${pad(mm)}`;
};

export const TimeField = React.forwardRef(function TimeField(
  { value = '', onChange, disabled, className, placeholder = 'hh:mm', testId, title },
  ref
) {
  const [text, setText] = React.useState(value);
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => { setText(value); }, [value]);

  const handleTextChange = (e) => {
    setText(e.target.value);
    const hhmm = parseToHHMM(e.target.value);
    if (hhmm) onChange?.(hhmm);
  };

  const handleBlur = () => {
    const hhmm = parseToHHMM(text);
    if (hhmm) setText(hhmm);
    else setText(value);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); handleBlur(); }
    if (e.key === 'Escape' && open) setOpen(false);
  };

  const handlePick = (t) => {
    setText(t);
    onChange?.(t);
    setOpen(false);
  };

  return (
    <div className={cn('relative inline-flex w-full items-center', className)}>
      <input
        ref={ref}
        type="text"
        inputMode="numeric"
        value={text}
        onChange={handleTextChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        data-testid={testId}
        title={title}
        className="h-9 w-full rounded-md border border-zinc-300 bg-white px-3 pr-10 text-sm placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            aria-label="時刻を開く"
            className="absolute right-1 inline-flex h-7 w-7 items-center justify-center rounded text-zinc-500 hover:bg-zinc-100 hover:text-zinc-800 disabled:opacity-50"
          >
            <Clock size={16} weight="duotone" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          className="w-32 max-h-60 overflow-y-auto p-1"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <ul className="space-y-0.5">
            {TIME_SLOTS.map((t) => (
              <li key={t}>
                <button
                  type="button"
                  onClick={() => handlePick(t)}
                  className={cn(
                    'w-full rounded px-2 py-1 text-left text-sm hover:bg-zinc-100',
                    t === value && 'bg-blue-50 text-blue-700 font-medium'
                  )}
                >
                  {t}
                </button>
              </li>
            ))}
          </ul>
        </PopoverContent>
      </Popover>
    </div>
  );
});
